import { useIntl } from '@capsicum/common';
import { BaseCheckboxInput as Checkbox } from '@capsicum/common-web';
import { CrossIcon } from '@capsicum/common-web/icons';
import { type PrimitiveAtom, useAtom } from 'jotai';
import { useCallback } from 'react';
import styled from 'styled-components';

export const GoogleAdsConsentModal = ({
	closeModal,
	isRelevantAdsAllowedAtom,
	isUserDataSharingAllowedAtom
}: {
	closeModal: () => void;
	isRelevantAdsAllowedAtom: PrimitiveAtom<boolean>;
	isUserDataSharingAllowedAtom: PrimitiveAtom<boolean>;
}) => {
	const { $t } = useIntl();
	const [isUserDataSharingAllowed, setIsUserDataSharingAllowed] = useAtom(isUserDataSharingAllowedAtom);
	const [isRelevantAdsAllowed, setIsRelevantAdsAllowed] = useAtom(isRelevantAdsAllowedAtom);

	const onDataBeingSentCheckedChange = useCallback(() => {
		setIsUserDataSharingAllowed(!isUserDataSharingAllowed);
	}, [isUserDataSharingAllowed, setIsUserDataSharingAllowed]);

	const onIsRelevantAdsCheckedChange = useCallback(() => {
		setIsRelevantAdsAllowed(!isRelevantAdsAllowed);
	}, [isRelevantAdsAllowed, setIsRelevantAdsAllowed]);

	return (
		<S.Wrapper>
			<S.HeaderContainer>
				<S.Title>{$t('onboard.googleConsent.dialogTitle')}</S.Title>
				<S.Close data-testid="google-consent-closeIcon" onClick={closeModal} />
			</S.HeaderContainer>
			<S.Description>{$t('onboard.googleConsent.dialogDescription')}</S.Description>
			<S.CheckboxesContainer>
				<Checkbox
					isStateHandledOutside
					testID="checkbox-data-being-sent"
					value={isUserDataSharingAllowed}
					onChange={onDataBeingSentCheckedChange}
				>
					<S.CheckboxTextContainer>
						<S.CheckboxText>{$t('onboard.googleConsent.checkboxTextDataSharing')}</S.CheckboxText>
					</S.CheckboxTextContainer>
				</Checkbox>
				<Checkbox
					isStateHandledOutside
					testID="checkbox-relevant-ads"
					value={isRelevantAdsAllowed}
					onChange={onIsRelevantAdsCheckedChange}
				>
					<S.CheckboxTextContainer>
						<S.CheckboxText>{$t('onboard.googleConsent.checkboxTextRelevantAds')}</S.CheckboxText>
					</S.CheckboxTextContainer>
				</Checkbox>
			</S.CheckboxesContainer>
			<S.Note>{$t('onboard.googleConsent.dialogNote')}</S.Note>
		</S.Wrapper>
	);
};

namespace S {
	export const Wrapper = styled.div`
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		align-items: center;
		text-align: center;
	`;

	export const HeaderContainer = styled.div`
		display: flex;
		width: 100%;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
		margin-bottom: ${({ theme }) => theme.spacings.large};
	`;

	export const Close = styled(CrossIcon)`
		color: ${({ theme }) => theme.colors.icons.main.unselected};
		cursor: pointer;
	`;

	export const CheckboxesContainer = styled.div`
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		align-items: flex-start;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const CheckboxTextContainer = styled.div`
		display: flex;
		flex: 1;
		flex-direction: row;
		width: 100%;
		align-items: center;
	`;

	export const Title = styled.span`
		${({ theme }) => theme.fonts.headings.title5}
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const Description = styled.span`
		margin-bottom: ${({ theme }) => theme.spacings.large};
		${({ theme }) => theme.fonts.text.text3}
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const Note = styled.span`
		margin-top: ${({ theme }) => theme.spacings.large};
		margin-bottom: ${({ theme }) => theme.spacings.large};
		${({ theme }) => theme.fonts.text.text5}
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const CheckboxText = styled.span`
		${({ theme }) => theme.fonts.text.text5};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		width: 100%;
		text-align: left;
	`;
}
